import { useEffect, useState } from "react";
import { AGENT_LOGIN, ANTHROPIC_MODELS, OPENAI_MODELS, modelLabel } from "../../shared/models";
import type { AgentModelEntryDTO, AgentModelsDTO, WhenSpent } from "../../shared/types";
import { get, put } from "../lib/api";
import { IconButton } from "./IconButton";
import { List, ListRow } from "./List";
import { TextLink } from "./TextLink";
import { Badge, Button, Choice, ErrorText, inputCls } from "./ui";

/** The models a subscription of each vendor may pay for, as the agent settings offer them. */
const OFFERED = { anthropic: ANTHROPIC_MODELS.filter((m) => !m.label.includes("(alias)")), openai: OPENAI_MODELS };

/**
 * An agent's model list for its own runs (AIT-64), in its settings: ordered entries, each a model and the subscription
 * that pays for it, and what happens when a subscription is spent: wait for it, or move to the next model. It offers only
 * the subscriptions the agent's owners own, and saves on its own, apart from the rest of the form.
 */
export function AgentModelList({ agentId }: { agentId: string }) {
  const [data, setData] = useState<AgentModelsDTO | null>(null);
  const [entries, setEntries] = useState<AgentModelEntryDTO[]>([]);
  const [whenSpent, setWhenSpent] = useState<WhenSpent>("wait");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const take = (d: AgentModelsDTO) => {
    setData(d);
    setEntries(d.entries);
    setWhenSpent(d.whenSpent);
  };
  useEffect(() => {
    get<AgentModelsDTO>(`/api/agents/${agentId}/models`).then(take, (e) => setError(e.message));
  }, [agentId]);
  if (!data) return <ErrorText error={error} />;
  const accountOf = (id: string) => data.accounts.find((a) => a.id === id);
  const edit = (i: number, e: AgentModelEntryDTO | null) => setEntries(e ? entries.map((x, j) => (j === i ? e : x)) : entries.filter((_, j) => j !== i));
  const move = (i: number, by: -1 | 1) => {
    const next = [...entries];
    [next[i], next[i + by]] = [next[i + by]!, next[i]!];
    setEntries(next);
  };
  const first = data.accounts[0];
  return (
    <List
      title="Models for its own runs"
      hint={`Its wakes, its schedules that run as itself and what those hand over run on the first of these that can take them, each paid by ${AGENT_LOGIN.a}. A run for a person never uses one.`}
      empty={
        data.accounts.length === 0 ? (
          <>No {AGENT_LOGIN.one} its owners own yet: add one on <TextLink to="/admin/models">Models</TextLink>.</>
        ) : (
          "None: its own runs wait until it has one, and no agent's own run can hand it work."
        )
      }
      footer={
        data.mayEdit ? (
          <div className="flex flex-wrap items-center gap-3">
            <Button
              size="sm"
              disabled={!first || entries.length >= 10}
              onClick={() => first && setEntries([...entries, { accountId: first.id, model: OFFERED[first.vendor][0]!.id }])}
            >
              + Add a model
            </Button>
            <Choice
              label={`When its ${AGENT_LOGIN.one} is spent`}
              value={whenSpent}
              onChange={setWhenSpent}
              options={[{ value: "wait", label: "Wait for it" }, { value: "next", label: "Move to the next model" }]}
            />
            <Button
              size="sm"
              variant="primary"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                setError(null);
                try {
                  take(await put<AgentModelsDTO>(`/api/agents/${agentId}/models`, { entries, whenSpent }));
                  setSaved(true);
                  setTimeout(() => setSaved(false), 2000);
                } catch (err) {
                  setError((err as Error).message);
                } finally {
                  setBusy(false);
                }
              }}
            >
              {busy ? "Saving…" : saved ? "Saved" : "Save models"}
            </Button>
            <ErrorText error={error} />
          </div>
        ) : (
          <span className="text-muted">Its models are for its owners to change; to change them, add yourself as an owner first.</span>
        )
      }
    >
      {entries.map((e, i) => {
        const account = accountOf(e.accountId);
        const vendor = account?.vendor ?? "anthropic";
        return (
          <ListRow
            key={i}
            leading={<span className="w-5 text-right text-xs text-faint tabular-nums">{i + 1}</span>}
            title={
              <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-2">
                <select
                  className={inputCls}
                  aria-label="Model"
                  disabled={!data.mayEdit}
                  value={e.model}
                  onChange={(ev) => edit(i, { ...e, model: ev.target.value })}
                >
                  {!OFFERED[vendor].some((m) => m.id === e.model) && <option value={e.model}>{modelLabel(e.model)}</option>}
                  {OFFERED[vendor].map((m) => (
                    <option key={m.id} value={m.id}>{m.label}</option>
                  ))}
                </select>
                <select
                  className={inputCls}
                  aria-label="Paid by"
                  disabled={!data.mayEdit}
                  value={e.accountId}
                  onChange={(ev) => {
                    const next = accountOf(ev.target.value);
                    if (!next) return;
                    // Another vendor's subscription pays for that vendor's models only.
                    edit(i, { accountId: next.id, model: next.vendor === vendor ? e.model : OFFERED[next.vendor][0]!.id });
                  }}
                >
                  {data.accounts.map((a) => (
                    <option key={a.id} value={a.id}>{a.label} ({a.ownerName})</option>
                  ))}
                </select>
              </div>
            }
            trailing={
              <>
                {account && account.status !== "signed_in" && <Badge tone="amber">{account.status === "spent" ? "spent" : account.status === "refused" ? "sign in again" : "signed out"}</Badge>}
                {account?.paused && <Badge tone="amber">paused</Badge>}
                {data.mayEdit && (
                  <>
                    <IconButton label="Move up" size="sm" disabled={i === 0} onClick={() => move(i, -1)}>↑</IconButton>
                    <IconButton label="Move down" size="sm" disabled={i === entries.length - 1} onClick={() => move(i, 1)}>↓</IconButton>
                    <IconButton label="Remove" size="sm" onClick={() => edit(i, null)}>✕</IconButton>
                  </>
                )}
              </>
            }
          />
        );
      })}
    </List>
  );
}
